import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import LoadingSpinner from 'components/LoadingSpinner';
import { Button, Chip, Table, TableColumn } from 'common/components';
import { useGetAccessManager, useGetTenantWorkflows } from 'services/hooks/queries';
import { UserRole } from 'types/userRoles';
import { User, WorkflowSummary } from 'types/accessManager.type';
import AssignWorkflowsModal from './AssignWorkflowsModal';
import { useTenantScope } from 'hooks';

import styles from '../ManagerCommandCenter.module.scss';

type Notification = {
    isVisible: boolean;
    message: string;
    type: 'success' | 'error';
};

const WorkflowAssignmentsTab: React.FC = () => {
    const { accessManagerData, isAccessManagerLoading, isAccessManagerFetching } = useGetAccessManager();
    const { tenantWorkflows, isTenantWorkflowsLoading, isTenantWorkflowsFetching } = useGetTenantWorkflows();
    const { isTenantScopeRequired } = useTenantScope();
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [notification, setNotification] = useState<Notification>({
        isVisible: false,
        message: '',
        type: 'success',
    });

    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ isVisible: true, message, type });
        setTimeout(() => {
            setNotification({ isVisible: false, message: '', type: 'success' });
        }, 5000);
    };

    const agentUsers = useMemo<User[]>(() => {
        return (accessManagerData?.users ?? []).filter((user) => user.roles.includes(UserRole.AGENT));
    }, [accessManagerData?.users]);

    const workflows: WorkflowSummary[] = tenantWorkflows ?? [];

    const columns: TableColumn<User>[] = [
        {
            key: 'name',
            title: 'Name',
            render: (_, record) => `${record.first_name} ${record.last_name}`,
        },
        {
            key: 'email',
            title: 'Email',
            dataIndex: 'email',
        },
        {
            key: 'workflows',
            title: 'Assigned Workflows',
            render: (_, record) => {
                const assignments = record.workflow_assignments ?? [];
                if (assignments.length === 0) {
                    return (
                        <Chip variant='filled' color='warning' size='sm'>
                            No assignments
                        </Chip>
                    );
                }

                return (
                    <div className={styles.workflowChipsCell}>
                        {assignments.map((wf) => (
                            <Chip key={wf.id} variant='filled' color='info' size='sm'>
                                {wf.title}
                            </Chip>
                        ))}
                    </div>
                );
            },
        },
        {
            key: 'actions',
            title: '',
            align: 'right',
            render: (_, record) => (
                <Button
                    variant='outline-secondary'
                    size='sm'
                    icon={Pencil}
                    onClick={() => setSelectedUser(record)}
                    disabled={workflows.length === 0 || isTenantScopeRequired}
                >
                    Edit Assignments
                </Button>
            ),
        },
    ];

    if (isAccessManagerLoading || isTenantWorkflowsLoading) {
        return (
            <div className='loading'>
                <LoadingSpinner size={80} borderWidth={8} color='#9b51e0' className='' />
            </div>
        );
    }

    return (
        <>
            {notification.isVisible && (
                <div
                    className={styles.assignmentsBanner}
                    style={{
                        borderLeftColor:
                            notification.type === 'success' ? 'var(--success-color)' : 'var(--error-color)',
                        backgroundColor:
                            notification.type === 'success' ? 'var(--success-color-alt)' : 'var(--bg-error)',
                    }}
                >
                    {notification.message}
                </div>
            )}

            {workflows.length === 0 && (
                <div className={styles.assignmentsBanner}>
                    <strong>No workflows in this tenant.</strong>{' '}
                    <Link to='/agentic-workflow-builder/new'>Create a workflow first</Link> before assigning agents.
                </div>
            )}

            <div className={styles.assignmentsHeader}>
                <h2>Workflow Assignments</h2>
            </div>

            <Table
                isLoading={isAccessManagerFetching || isTenantWorkflowsFetching}
                data={agentUsers}
                columns={columns}
                rowKey='id'
                hoverable
                variant='secondary'
                emptyText='No agents found'
            />

            {selectedUser && (
                <AssignWorkflowsModal
                    isOpen={!!selectedUser}
                    user={selectedUser}
                    workflows={workflows}
                    onClose={() => setSelectedUser(null)}
                    onSuccess={(message) => {
                        showNotification(message, 'success');
                        setSelectedUser(null);
                    }}
                    onError={(message) => showNotification(message, 'error')}
                />
            )}
        </>
    );
};

export default WorkflowAssignmentsTab;
